import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'
import EventParticipantStatusItems from '../../TeamEvent/components/EventParticipantStatusItems'
import EventParticipantEditPopover from '../../TeamEvent/components/EventParticipantEditPopover'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { getSpecialization } from 'app/frontend/base/services/characterValues'
import { Loader2, MessageCircleMore } from 'lucide-react'
import { classColours } from 'app/frontend/types/custom/CharacterClass'

type Props = {
  main?: wowaudit.TeamMembership
  participant?: wowaudit.EventParticipantReference
  status: wowaudit.EventParticipantReference['status']
  canEditParticipant: boolean
  selectableStatuses: wowaudit.EventParticipantReference['status'][]
  isUpdatingStatus: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (nextStatus: wowaudit.EventParticipantReference['status']) => void
  onSaveWithComment?: (
    nextStatus: wowaudit.EventParticipantReference['status'],
    comment: string,
  ) => void
  triggerClassName: string
  nonEditableClassName?: string
  stopPropagation?: boolean
  iconSize?: 'tiny' | 'small' | 'medium'
  showCommentIndicator?: boolean
}

const EventParticipantStatusControl = ({
  main,
  participant,
  status,
  canEditParticipant,
  selectableStatuses,
  isUpdatingStatus,
  open,
  onOpenChange,
  onSelect,
  onSaveWithComment,
  triggerClassName,
  nonEditableClassName = 'flex justify-center',
  stopPropagation = false,
  iconSize,
  showCommentIndicator = true,
}: Props) => {
  const {
    data: { user },
  } = usePageInfo()
  const isOwnCharacter =
    !!main && !!user && user.id === main.characterReference.userId
  const participantDisplayCharacter =
    participant?.altCharacterReference ?? participant?.characterReference
  const participantDisplayMeta =
    participant && participantDisplayCharacter
      ? {
          id: participantDisplayCharacter.id,
          name: participantDisplayCharacter.name,
          characterClass: participantDisplayCharacter.characterClass,
          role: participant.role,
          specName:
            participant.specId == null
              ? undefined
              : getSpecialization(participant.specId.toString())?.name,
          specIcon:
            participant.specId == null
              ? undefined
              : getSpecialization(participant.specId.toString())?.icon,
        }
      : undefined
  const usesDifferentCharacter =
    !!participantDisplayMeta &&
    !!main &&
    participantDisplayMeta.id !== main.characterReference.id
  const hasSpecOverride =
    participant?.specId != null && participant.specId !== main?.specId
  const statusIcon = isUpdatingStatus ? (
    <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
  ) : (
    <EventStatusIcon status={status} size={iconSize ?? 'small'} />
  )
  const showParticipantIcon =
    !!participantDisplayMeta &&
    (usesDifferentCharacter || (isOwnCharacter && hasSpecOverride))
  const showSpecIcon =
    isOwnCharacter && hasSpecOverride && !usesDifferentCharacter
  const iconGroup =
    showParticipantIcon && participantDisplayMeta ? (
      <div className='flex items-center justify-center gap-1'>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className='inline-flex mr-1'>
              {showSpecIcon && participantDisplayMeta.specIcon ? (
                <BlizzardIcon
                  iconName={participantDisplayMeta.specIcon}
                  size='medium'
                />
              ) : (
                <CharacterClassIcon
                  size='medium'
                  characterClass={participantDisplayMeta.characterClass}
                />
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div className='flex items-center gap-1.5 justify-center'>
              <CharacterRoleIcon
                role={participantDisplayMeta.role}
                size='small'
              />
              <span
                className={
                  classColours[participantDisplayMeta.characterClass].text
                }
              >
                {participantDisplayMeta.name}
              </span>
            </div>
            <div className='text-xs text-muted-foreground text-center'>
              {participantDisplayMeta.specName
                ? `${participantDisplayMeta.specName} ${participantDisplayMeta.characterClass}`
                : participantDisplayMeta.characterClass}
            </div>
          </TooltipContent>
        </Tooltip>
        {statusIcon}
      </div>
    ) : (
      statusIcon
    )

  const hasComment =
    showCommentIndicator && !!participant?.comment?.trim().length
  const wrappedIconGroup = hasComment ? (
    <span className='relative inline-flex'>
      {iconGroup}
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='absolute -right-6.5 -bottom-1.5 rounded-full bg-card p-0.5'>
            <MessageCircleMore className='w-3.5 h-3.5 text-accent-foreground' />
          </span>
        </TooltipTrigger>
        <TooltipContent className='max-w-[300px] wrap-break-word'>
          {participant.comment}
        </TooltipContent>
      </Tooltip>
    </span>
  ) : (
    iconGroup
  )

  const stopEvent = (eventClick: { stopPropagation: () => void }) => {
    if (stopPropagation) {
      eventClick.stopPropagation()
    }
  }

  if (!canEditParticipant) {
    return (
      <div className={nonEditableClassName}>
        <span className={triggerClassName}>{wrappedIconGroup}</span>
      </div>
    )
  }

  if (onSaveWithComment) {
    return (
      <div className={nonEditableClassName} onClick={stopEvent}>
        <EventParticipantEditPopover
          open={open}
          onOpenChange={onOpenChange}
          trigger={
            <button
              className={triggerClassName}
              disabled={isUpdatingStatus}
              onClick={stopEvent}
            >
              {wrappedIconGroup}
            </button>
          }
          selectableStatuses={selectableStatuses}
          currentStatus={status}
          currentComment={participant?.comment}
          isSaving={isUpdatingStatus}
          onSave={(nextStatus, comment) =>
            onSaveWithComment(nextStatus, comment)
          }
          align='center'
        />
      </div>
    )
  }

  return (
    <div className={nonEditableClassName} onClick={stopEvent}>
      <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
        <DropdownMenuTrigger
          className={triggerClassName}
          disabled={isUpdatingStatus}
          onClick={stopEvent}
        >
          {wrappedIconGroup}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className='w-fit p-1'
          side='bottom'
          align='center'
          onClick={stopEvent}
        >
          <EventParticipantStatusItems
            currentStatus={status}
            statuses={selectableStatuses}
            onSelect={onSelect}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export default EventParticipantStatusControl
